'use client';

import React from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { formatDate } from '@hub/common/src/format-utils';

import type { CitOrigin, Properties } from '@hub/common/src/types/cit-public-api';

import { getEntryLink, getProfileInstanceLink } from '@hub/common/src/navigationUtils';
import { useCitOriginClient } from '@hub/common-components/src/CitOriginClientProvider';
import { useCitThemeClient } from './CitThemeProvider';
import type { ReferenceType } from './ReferenceOverlay';

interface PropertiesViewProps {
  properties: Properties | undefined;
  accountId: string;
  setSelectedReference?: Dispatch<SetStateAction<ReferenceType | undefined>>;
  currentReference?: ReferenceType | null;
}

type TextStyles = React.CSSProperties;

interface FormElementProps {
  label?: string | undefined;
  width?: string | undefined;
  content: React.ReactNode;
  caption?: boolean;
  nestLevel?: number;
  textStyles?: TextStyles;
}

const FormElement = ({ label, content, width, nestLevel = 0, textStyles }: FormElementProps) => {
  // widths are specified in mui's grid system, need to convert to tailwind widths
  const widths = width?.split(' ');
  const widthClasses =
    widths
      ?.map(
        (w) =>
          // target mobile screens and up first i.e w-xs-12 -> w-full and then override for larger screens and when there are multiple widths
          //  specified i.e w-md-6 -> lg:w-[50%]
          `${widths.length > 1 && w.split('-')[1] !== 'xs' ? 'lg:' : ''}w-${
            w.split('-')[2] === '12' ? 'full' : `${w.split('-')[2]}/12`
          }`
      )
      // default to full width if no widths are specified
      .join(' ') || 'w-full';

  return (
    <div className={`mb-4 pr-6 ${widthClasses}`}>
      {label ? (
        // Create header element based solely on nesting level, starting from h2 (skipping h1 for title)
        nestLevel < 5 ? (
          React.createElement(
            'h' + (nestLevel + 2), // h2 for nestLevel 0, h3 for nestLevel 1, h4 for nestLevel 2, etc.
            {
              className: `${nestLevel > 0 ? 'text-lg' : 'text-xl'} color-inherit`,
              style: textStyles
            },
            label
          )
        ) : (
          <p
            className={`${nestLevel > 0 ? 'text-lg' : 'text-xl'} color-inherit`}
            style={textStyles}
          >
            {label}
          </p>
        )
      ) : null}
      {content}
    </div>
  );
};

interface FormGroupProps {
  label?: string | undefined;
  children: React.ReactNode;
  nestLevel: number;
  isContainer?: boolean;
  textStyles?: TextStyles;
}

const FormGroup = ({
  label,
  children,
  nestLevel,
  isContainer = false,
  textStyles
}: FormGroupProps) => (
  <div className='w-full color-inherit'>
    {label ? (
      // Create header element based solely on nesting level, starting from h2 (skipping h1 for title)
      nestLevel < 5 ? (
        React.createElement(
          'h' + (nestLevel + 2), // h2 for nestLevel 0, h3 for nestLevel 1, h4 for nestLevel 2, etc.
          {
            className: `${
              nestLevel > 0 ? `text-lg ${isContainer ? 'font-bold' : ''}` : 'text-xl font-bold'
            } color-inherit`,
            style: textStyles
          },
          label
        )
      ) : (
        <p
          className={`${
            nestLevel > 0 ? `text-lg ${isContainer ? 'font-bold' : ''}` : 'text-xl font-bold'
          } color-inherit ${isContainer ? 'font-bold' : ''}`}
          style={textStyles}
        >
          {label}
        </p>
      )
    ) : null}
    <div className='flex flex-wrap color-inherit'>{children}</div>
  </div>
);

type SimpleContentValue = React.ReactNode;

const SimpleContent = ({
  value,
  textStyles
}: {
  value: SimpleContentValue | SimpleContentValue[];
  textStyles?: TextStyles;
}) =>
  Array.isArray(value) ? (
    <React.Fragment>
      {value.map((v, idx) => (
        <span className='whitespace-pre-wrap inline-block text-sm' key={idx} style={textStyles}>
          {v}
          {idx !== value.length - 1 ? ', ' : ''}
        </span>
      ))}
    </React.Fragment>
  ) : (
    <span className='whitespace-pre-wrap inline-block text-sm' style={textStyles}>
      {value}
    </span>
  );

interface FuzzyDate {
  fuzzydate_value: string;
}

interface FuzzyDateRange {
  fuzzydate_from: FuzzyDate;
  fuzzydate_to: FuzzyDate;
}

const formatFuzzyDateRange = (fuzzyDate: FuzzyDateRange) => {
  const { fuzzydate_from, fuzzydate_to } = fuzzyDate;
  return `${(fuzzydate_from && fuzzydate_from.fuzzydate_value) || ''}${
    fuzzydate_from && fuzzydate_to ? ' - ' : ''
  }${(fuzzydate_to && fuzzydate_to.fuzzydate_value) || ''}`;
};

interface WebLink {
  weblink_value?: string;
  weblink_link: string;
}

const formatWebLink = (weblink: WebLink, textStyles?: TextStyles) => {
  const { weblink_value, weblink_link } = weblink;
  return (
    <a
      href={weblink_link}
      className='underline'
      style={{ color: 'inherit', ...textStyles }}
      target='_blank'
      rel='noopener noreferrer'
    >
      {weblink_value || weblink_link}
    </a>
  );
};

interface ReferenceFormatType {
  reference_value: string;
  reference_link: string;
  reference_type: string;
}

const formatReference = (
  incomingReference: ReferenceFormatType,
  accountId: string,
  citOrigin: CitOrigin,
  referenceType: 'profile' | 'entry',
  setSelectedReference?: Dispatch<SetStateAction<ReferenceType | undefined>> | undefined,
  currentReference?: ReferenceType | null | undefined
) => {
  const { reference_value, reference_link, reference_type } = incomingReference;

  const handleReferenceClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    setSelectedReference?.({
      referenceLink: reference_link,
      referenceType,
      profileTypeURI: reference_type
    });
  };

  return (
    <>
      {/* only show the link if the current reference is not the same as the reference being linked to */}
      {/* and that the setSelectedReference is provided, that means the reference overlay is not open */}
      {setSelectedReference && currentReference?.referenceLink !== reference_link ? (
        <div className='underline cursor-pointer inline-flex items-center gap-1'>
          {/* purposefully using <a> instead of <Link> to prevent interceptor route from being triggered */}
          <a
            className='flex items-center gap-1'
            href={
              referenceType === 'profile'
                ? getProfileInstanceLink(
                    reference_type,
                    reference_link,
                    citOrigin,
                    accountId,
                    true,
                    true
                  )
                : getEntryLink(reference_link, citOrigin, accountId)
            }
            onClick={handleReferenceClick}
          >
            {reference_value}
          </a>
        </div>
      ) : (
        <p>{reference_value}</p>
      )}
    </>
  );
};

const formElementFactory = (
  property: Properties,
  valueType: string,
  label: string,
  key: string,
  nestLevel = 0,
  accountId: string,
  citOrigin: CitOrigin,
  setSelectedReference?: Dispatch<SetStateAction<ReferenceType | undefined>>,
  currentReference?: ReferenceType | null,
  textStyles?: TextStyles
) => {
  const { width } = property;
  switch (valueType) {
    case 'value_text': {
      const { value_text } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={<SimpleContent value={value_text} textStyles={textStyles} />}
          nestLevel={nestLevel}
          textStyles={textStyles}
        />
      );
    }
    case 'value_integer': {
      const { value_integer } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={<SimpleContent value={value_integer} textStyles={textStyles} />}
          nestLevel={nestLevel}
          textStyles={textStyles}
        />
      );
    }
    case 'value_decimal': {
      const { value_decimal } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={<SimpleContent value={value_decimal.decimal_value} textStyles={textStyles} />}
          nestLevel={nestLevel}
          textStyles={textStyles}
        />
      );
    }
    case 'value_date': {
      const { value_date } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_date)
                  ? value_date.map((d) => formatDate(d))
                  : formatDate(value_date)
              }
              textStyles={textStyles}
            />
          }
          nestLevel={nestLevel}
          textStyles={textStyles}
        />
      );
    }
    case 'value_length': {
      const { value_length } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_length)
                  ? value_length.map((l: { length_value: string }) => l.length_value)
                  : value_length.length_value
              }
              textStyles={textStyles}
            />
          }
          nestLevel={nestLevel}
          textStyles={textStyles}
        />
      );
    }
    case 'value_weight': {
      const { value_weight } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_weight)
                  ? value_weight.map((w: { weight_value: string }) => w.weight_value)
                  : value_weight.weight_value
              }
              textStyles={textStyles}
            />
          }
          nestLevel={nestLevel}
          textStyles={textStyles}
        />
      );
    }
    case 'value_reference': {
      const { value_reference } = property;

      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_reference)
                  ? value_reference.map((r: ReferenceFormatType) =>
                      r.reference_value && r.reference_link
                        ? formatReference(
                            r,
                            accountId,
                            citOrigin,
                            'profile',
                            setSelectedReference,
                            currentReference
                          )
                        : r.reference_value
                    )
                  : value_reference.reference_value && value_reference.reference_link
                    ? formatReference(
                        value_reference,
                        accountId,
                        citOrigin,
                        'profile',
                        setSelectedReference,
                        currentReference
                      )
                    : value_reference.reference_value
              }
              textStyles={textStyles}
            />
          }
          nestLevel={nestLevel}
          textStyles={textStyles}
        />
      );
    }

    case 'value_entry_reference': {
      const { value_entry_reference } = property;

      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_entry_reference)
                  ? value_entry_reference.map((r: ReferenceFormatType) =>
                      r.reference_value && r.reference_link
                        ? formatReference(
                            r,
                            accountId,
                            citOrigin,
                            'entry',
                            setSelectedReference,
                            currentReference
                          )
                        : r.reference_value
                    )
                  : value_entry_reference.reference_value && value_entry_reference.reference_link
                    ? formatReference(
                        value_entry_reference,
                        accountId,
                        citOrigin,
                        'entry',
                        setSelectedReference,
                        currentReference
                      )
                    : value_entry_reference.reference_value
              }
            />
          }
          nestLevel={nestLevel}
        />
      );
    }

    case 'value_fuzzydate': {
      const { value_fuzzydate } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_fuzzydate)
                  ? value_fuzzydate.map((d: { fuzzydate_value: string }) => d.fuzzydate_value)
                  : value_fuzzydate.fuzzydate_value
              }
              textStyles={textStyles}
            />
          }
          nestLevel={nestLevel}
          textStyles={textStyles}
        />
      );
    }
    case 'value_fuzzydaterange': {
      const { value_fuzzydaterange } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_fuzzydaterange)
                  ? value_fuzzydaterange.map((d: FuzzyDateRange) => formatFuzzyDateRange(d))
                  : formatFuzzyDateRange(value_fuzzydaterange)
              }
              textStyles={textStyles}
            />
          }
          nestLevel={nestLevel}
          textStyles={textStyles}
        />
      );
    }
    case 'value_currency': {
      const { value_currency } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_currency)
                  ? value_currency.map((c: { currency_value: string }) => c.currency_value)
                  : value_currency.currency_value
              }
              textStyles={textStyles}
            />
          }
          nestLevel={nestLevel}
          textStyles={textStyles}
        />
      );
    }
    case 'value_duration': {
      const { value_duration } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_duration)
                  ? value_duration.map((c: { duration_value: string }) => c.duration_value)
                  : value_duration.duration_value
              }
              textStyles={textStyles}
            />
          }
          nestLevel={nestLevel}
          textStyles={textStyles}
        />
      );
    }
    case 'value_percent': {
      const { value_percent } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_percent)
                  ? value_percent.map((c: { percent_value: string }) => c.percent_value)
                  : value_percent.percent_value
              }
              textStyles={textStyles}
            />
          }
          nestLevel={nestLevel}
          textStyles={textStyles}
        />
      );
    }
    case 'value_weblink': {
      const { value_weblink } = property;
      return (
        <FormElement
          key={key}
          label={label}
          width={width}
          content={
            <SimpleContent
              value={
                Array.isArray(value_weblink)
                  ? value_weblink.map((c: WebLink) => formatWebLink(c, textStyles))
                  : formatWebLink(value_weblink, textStyles)
              }
              textStyles={textStyles}
            />
          }
          nestLevel={nestLevel}
          textStyles={textStyles}
        />
      );
    }
    default:
      console.log(`NO HANDLER FOR ${valueType}`);
      return null;
  }
};

const MAX_DEPTH = 20;

const propertiesViewMaker = (
  properties: Properties,
  keyPrefix: string,
  nestLevel: number,
  accountId: string,
  citOrigin: CitOrigin,
  setSelectedReference?: Dispatch<SetStateAction<ReferenceType | undefined>>,
  currentReference?: ReferenceType | null,
  textStyles?: TextStyles
) => {
  if (nestLevel > MAX_DEPTH) {
    return [];
  }

  const values = Object.values<Properties[keyof Properties]>(properties);
  const countValues = values.length;
  return values.reduce((acc, property, outerIndex) => {
    let { label } = property;
    // skip label if same as parent key and there's only one property
    if (keyPrefix.startsWith(label) && outerIndex === 0 && countValues === 1) {
      label = undefined;
    }
    // find value_* key
    const valueKey = Object.keys(property).find((k) => k.startsWith('value'));
    // if no valueKey found then just ignore property
    if (!valueKey) {
      return acc;
    }
    switch (valueKey) {
      case 'value_hreference_list': {
        const { value_hreference_list } = property;
        const children: React.ReactNode[] = [];
        if (Array.isArray(value_hreference_list)) {
          value_hreference_list.forEach((v, innerIndex) => {
            const fe = formElementFactory(
              v,
              'value_reference',
              v.label,
              `${label}-${innerIndex}`,
              nestLevel,
              accountId,
              citOrigin,
              setSelectedReference,
              currentReference,
              textStyles
            );
            if (fe) {
              children.push(fe);
            }
          });
        } else {
          const fe = formElementFactory(
            value_hreference_list,
            'value_reference',
            label
              ? label.startsWith(value_hreference_list.label)
                ? undefined
                : value_hreference_list.label
              : undefined,
            `${label}-${outerIndex}`,
            nestLevel,
            accountId,
            citOrigin,
            setSelectedReference,
            currentReference,
            textStyles
          );
          if (fe) {
            children.push(fe);
          }
        }
        acc.push(
          <FormGroup
            key={`${keyPrefix}-${outerIndex}`}
            label={label}
            nestLevel={nestLevel}
            textStyles={textStyles}
          >
            {children}
          </FormGroup>
        );
        break;
      }
      case 'value_reference': {
        const { value_reference } = property;
        // NOTE: Special handling for Profile projections.  Check if any Profile has projections
        // and if so tree each profile (there might be more than one) as a separate form element with
        // their own labeling
        const valueReferenceList = Array.isArray(value_reference)
          ? value_reference
          : [value_reference];
        if (valueReferenceList.some((v) => 'projections' in v)) {
          for (const [listIdx, valueRef] of valueReferenceList.entries()) {
            property.value_reference = valueRef;
            acc.push(
              formElementFactory(
                property,
                valueKey,
                label,
                `${keyPrefix}-${outerIndex}-${listIdx}`,
                nestLevel + 1,
                accountId,
                citOrigin,
                setSelectedReference,
                currentReference,
                textStyles
              )
            );
            if (valueRef.projections) {
              acc.push(
                ...propertiesViewMaker(
                  valueRef.projections,
                  `${label}-${outerIndex}-${listIdx}`,
                  nestLevel + 1,
                  accountId,
                  citOrigin,
                  setSelectedReference,
                  currentReference,
                  textStyles
                )
              );
            }
          }
        } else {
          acc.push(
            formElementFactory(
              property,
              valueKey,
              label,
              `${keyPrefix}-${outerIndex}`,
              nestLevel,
              accountId,
              citOrigin,
              setSelectedReference,
              currentReference,
              textStyles
            )
          );
        }
        break;
      }
      case 'value': {
        const { value } = property;
        const children: React.ReactNode[] = [];
        if (Array.isArray(value)) {
          for (const [idx, v] of value.entries()) {
            children.push(
              ...propertiesViewMaker(
                v,
                `${label}-${idx}`,
                nestLevel + 1,
                accountId,
                citOrigin,
                setSelectedReference,
                currentReference,
                textStyles
              )
            );
          }
        } else {
          // peek at the sub-properties.  If there's only one and it's label is "similar" to ours
          // then suppress our label and use our only child's label
          const childKeys = Object.keys(value);
          if (childKeys.length === 1) {
            if (label.startsWith(value[childKeys[0]].label)) {
              label = '';
            }
          }
          children.push(
            ...propertiesViewMaker(
              value,
              label,
              nestLevel + 1,
              accountId,
              citOrigin,
              setSelectedReference,
              currentReference,
              textStyles
            )
          );
        }
        acc.push(
          <FormGroup
            key={`${keyPrefix}-${outerIndex}`}
            label={label}
            nestLevel={nestLevel}
            isContainer={true}
            textStyles={textStyles}
          >
            {children}
          </FormGroup>
        );
        break;
      }
      default:
        acc.push(
          formElementFactory(
            property,
            valueKey,
            label,
            `${keyPrefix}-${outerIndex}`,
            nestLevel,
            accountId,
            citOrigin,
            setSelectedReference,
            currentReference,
            textStyles
          )
        );
    }
    return acc;
  }, []);
};

export default function PropertiesView({
  properties,
  accountId,
  setSelectedReference,
  currentReference
}: PropertiesViewProps) {
  const citOrigin = useCitOriginClient();

  const { accountTheme } = useCitThemeClient();

  const textStyles: TextStyles = {
    fontFamily: accountTheme.bodyFont(),
    lineHeight: accountTheme.lineHeight(),
    letterSpacing: accountTheme.letterSpacing()
  };

  return (
    <div className='mt-2 flex flex-wrap color-inherit' style={textStyles}>
      {properties
        ? propertiesViewMaker(
            properties,
            'root',
            0,
            accountId,
            citOrigin,
            setSelectedReference,
            currentReference,
            textStyles
          )
        : null}
    </div>
  );
}
